import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import InvoiceTemplateWave from './InvoiceTemplateWave';
import InvoiceTemplateDexter from './InvoiceTemplateDexter';
import InvoiceTemplateModerno from './InvoiceTemplateModerno';
import InvoiceTemplateCuentaCobro from './InvoiceTemplateCuentaCobro';
import InvoiceTemplateEjecutiva from './InvoiceTemplateEjecutiva';
import { formatCurrency as formatCurrencyFn, formatDate as formatDateFn } from '../utils/formatters';

const InvoicePreview = ({ invoice, template, companyInfo, templateColor }) => {
  const { t } = useTranslation();
  
  // Usar el color de la plantilla o el color personalizado
  const activeColor = templateColor || template?.color || '#84cc16';

  // Si el documento es Cuenta de Cobro (bill), usar plantilla especial
  if (invoice?.documentType === 'bill') {
    return <InvoiceTemplateCuentaCobro invoice={invoice} template={template} companyInfo={companyInfo || invoice?.from} templateColor={activeColor} />;
  }

  // Si la plantilla es de tipo "wave", usar el componente especializado
  if (template?.type === 'wave') {
    return <InvoiceTemplateWave invoice={invoice} template={template} templateColor={activeColor} />;
  }

  // Si la plantilla es de tipo "dexter", usar el componente Dexter
  if (template?.type === 'dexter') {
    return <InvoiceTemplateDexter invoice={invoice} template={template} templateColor={activeColor} />;
  }

  // Si la plantilla es de tipo "moderno", usar el componente Moderno
  if (template?.type === 'moderno') {
    return <InvoiceTemplateModerno invoice={invoice} template={template} templateColor={activeColor} />;
  }

  // Si la plantilla es de tipo "ejecutiva", usar el componente Ejecutiva
  if (template?.type === 'ejecutiva') {
    return <InvoiceTemplateEjecutiva invoice={invoice} template={template} templateColor={activeColor} />;
  }

  // Safe access to nested properties
  const from = invoice?.from || invoice?.fromAddress || {};
  const to = invoice?.to || invoice?.toAddress || {};
  const items = invoice?.items || [];
  const isPaid = invoice?.status === 'paid' && !['quotation', 'proforma'].includes(invoice?.documentType);

  // Función para formatear números según el país del emisor (#30.c)
  const userCountry = from?.country;
  const formatCurrency = (value) =>
    formatCurrencyFn(value, { country: userCountry });

  const getDocumentTitle = (type) => {
    return t(`documentTypes.${type}`, type?.toUpperCase() || 'FACTURA');
  };

  // Usar el color de la plantilla seleccionada
  const displayColor = activeColor || template?.color || '#2563eb';

  return (
    <Card className="p-8 bg-white shadow-lg relative overflow-hidden">
      {/* Sello de PAGADO */}
      {isPaid && (
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: 10 }}
        >
          <div 
            className="border-8 border-red-500 text-red-500 font-bold text-6xl px-8 py-4 rounded-lg opacity-40"
            style={{ 
              transform: 'rotate(-25deg)',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            PAGADO
          </div>
        </div>
      )}
      
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b pb-6" style={{ borderColor: displayColor }}>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold" style={{ color: displayColor }}>
                {getDocumentTitle(invoice?.documentType || 'invoice')}
              </h1>
              <p className="text-gray-600 mt-2">#{invoice?.number || invoice?.invoiceNumber || 'S/N'}</p>
            </div>
            <div className="text-right">
              {invoice?.logo ? (
                <img 
                  src={invoice.logo} 
                  alt="Company Logo" 
                  className="h-24 w-24 object-contain"
                />
              ) : (
                <div className="w-24 h-24 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                  LOGO
                </div>
              )}
            </div>
          </div>
        </div>

        {/* From & To */}
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-2">{t('preview.from')}</h3>
            <div className="text-sm">
              <p className="font-bold text-gray-900">{from.name || ''}</p>
              {from.nit && <p className="text-gray-600">NIT: {from.nit}</p>}
              <p className="text-gray-600">{from.email || ''}</p>
              <p className="text-gray-600">{from.phone || ''}</p>
              <p className="text-gray-600">{from.address || ''}</p>
              <p className="text-gray-600">
                {from.city || ''}{from.city && from.state ? ', ' : ''}{from.state || ''} {from.zip || ''}
              </p>
              <p className="text-gray-600">{from.country || ''}</p>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-500 mb-2">{t('preview.billTo')}</h3>
            <div className="text-sm">
              <div className="text-gray-900 whitespace-pre-line">{to.name || ''}</div>
            </div>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="text-gray-500">{t('preview.invoiceDate')}</p>
            <p className="font-semibold text-gray-900">{invoice?.date ? formatDateFn(invoice.date, { country: userCountry }) : ''}</p>
          </div>
          <div>
            <p className="text-gray-500">{t('preview.dueDate')}</p>
            <p className="font-semibold text-gray-900">{invoice?.dueDate ? formatDateFn(invoice.dueDate, { country: userCountry }) : ''}</p>
          </div>
        </div>

        {/* Items Table */}
        <div>
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="border-b-2" style={{ borderColor: displayColor }}>
                <th className="text-center py-2 font-semibold text-gray-700 w-10">Item</th>
                <th className="text-left py-2 font-semibold text-gray-700">{t('preview.description')}</th>
                <th className="text-center py-2 font-semibold text-gray-700 w-14">{t('preview.qty')}</th>
                <th className="text-right py-2 font-semibold text-gray-700 w-24">{t('preview.rate')}</th>
                <th className="text-right py-2 font-semibold text-gray-700 w-28">{t('preview.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className="border-b border-gray-200">
                  <td className="py-3 text-center text-gray-500">{index + 1}</td>
                  <td className="py-3 text-gray-900" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item?.description || ''}</td>
                  <td className="py-3 text-center text-gray-700">{item?.quantity || 0}</td>
                  <td className="py-3 text-right text-gray-700">${formatCurrency(item?.rate)}</td>
                  <td className="py-3 text-right font-semibold text-gray-900">${formatCurrency(item?.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('preview.subtotal')}:</span>
              <span className="font-semibold text-gray-900">${formatCurrency(invoice?.subtotal)}</span>
            </div>
            {invoice?.hasTax && invoice?.taxRate > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{invoice?.taxName || t('invoice.tax')} ({invoice?.taxRate}%):</span>
                <span className="font-semibold text-gray-900">${formatCurrency(invoice?.tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t-2" style={{ borderColor: displayColor }}>
              <span>{t('preview.total')}:</span>
              <span style={{ color: displayColor }}>${formatCurrency(invoice?.total)}</span>
            </div>
            {/* #Abonos — Cantidad pagada y Saldo debido (solo si hay abonos) */}
            {Number(invoice?.totalPaid) > 0 && !isPaid && (
              <>
                <div className="flex justify-between text-sm pt-2 text-gray-600">
                  <span>{t('preview.amountPaid')}:</span>
                  <span>${formatCurrency(invoice.totalPaid)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-1">
                  <span>{t('preview.balanceDue')}:</span>
                  <span className="text-gray-900">
                    ${formatCurrency((Number(invoice?.total) || 0) - (Number(invoice.totalPaid) || 0))}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Notes */}
        {invoice?.notes && (
          <div className="pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('preview.notes')}</h4>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Terms */}
        {invoice?.terms && (
          <div className="pt-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">{t('preview.terms')}</h4>
            <p className="text-sm text-gray-600">{invoice.terms}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default InvoicePreview;
